import React, { FC, useState } from "react";

import {
  useApiMembersEmployeeConditionSubmissionsSubmissionRepliesIndex,
  useRightSidebarState,
  useSearchManagersEmployeeConditionSubmissions,
} from "~/hooks";

import {
  EmployeeLayout,
  EmployeeLayoutMainContent,
  EmployeeLayoutRightSidebar,
} from "~/components/layouts";
import {
  DivisionAndSectionAndEmployeesDropdownField,
  EmployeeConditionSubmissionDetail,
  EmployeeConditionSubmissionRepliesWithEmployeeConditionSubmission,
  SelectDateRangeButtons,
} from "~/components/organisms";

import {
  Button,
  DropDownField,
  Form,
  FormSubmitButton,
  MultipleDropDownField,
  TextField,
} from "~/ui";
import {
  FilterIconWithSortMenu,
  InfiniteScrollWithMoreButton,
  LabelWithCheckBox,
  RightSidebarToggleIconWithLabel,
  TitleWithChild,
} from "~/ui";

export const ManagersEmployeeConditionSubmissionsIndex: FC = () => {
  const query = new URLSearchParams(location.search);
  const businessSectionId = query.get("business_section_id") || "";

  const {
    searchOpenToggle,
    setClose,
    isOpen,
    isReplyOpen,
    isSearchOpen,
    setReplyOpen,
    currentOpenLabel,
  } = useRightSidebarState("isClose");
  const [selectGetReplyId, setSelectGetReplyId] = useState<string>("");
  const { data: repliesIndexData, refetch: repliesRefetch } =
    useApiMembersEmployeeConditionSubmissionsSubmissionRepliesIndex({
      employeeConditionSubmissionId: selectGetReplyId,
      config: {
        enabled: Boolean(selectGetReplyId),
      },
    });

  const breadCrumbItems = [
    {
      label: "コンディション確認",
      href: `/managers/employee_condition_submissions?business_section_id=${businessSectionId}`,
    },
  ];

  const {
    sort,
    setSort,
    keyword,
    isFetching,
    fetchNextPage,
    hasNextPage,
    startDate,
    endDate,
    employeeConditionSubmissions,
    onChangeKeyword,
    onSearchSubmit,
    onConditionReset,
    onChangeDateRange,
    selectableDropdownHasBookmarkIds,
    selectedDropdownHasBookmarkId,
    onChangeDropdownHasBookmarkId,
    selectableDropdownHasReplyIds,
    selectedDropdownHasReplyId,
    onChangeDropdownHasReplyId,
    refetch,
    selectableDropdownEmployees,
    selectedDropdownEmployees,
    selectableDropdownDivisions,
    selectedDropdownDivisions,
    selectableDropdownSections,
    selectedDropdownSections,
    onDivisionChange,
    onSectionChange,
    onEmployeesChange,
    selectableEmployeeConditionSurveys,
    selectedEmployeeConditionSurveys,
    onChangeEmployeeConditionSurveys,
    isBelongingEmployee,
    onChangeIsBelongingEmployee,
  } = useSearchManagersEmployeeConditionSubmissions();

  return (
    <EmployeeLayout withRightSidebar breadCrumbItems={breadCrumbItems}>
      <EmployeeLayoutMainContent navigations={[]} withRightSidebar>
        <FilterIconWithSortMenu
          likeSort={() => setSort("likeCountDesc")}
          bookmarkSort={() => setSort("bookmarkCountDesc")}
          createdAtAscSort={() => setSort("createdAtAsc")}
          createdAtDescSort={() => setSort("createdAtDesc")}
          currentSortType={sort}
          className="mt-6"
        />
        <InfiniteScrollWithMoreButton
          itemsLength={employeeConditionSubmissions.length}
          nextFetchFunction={fetchNextPage}
          hasMore={hasNextPage}
          isLoading={isFetching}
          className="border-t-2 border-secondary-200"
          noContentMessage="回答がありません"
        >
          {employeeConditionSubmissions.map((submission) => (
            <EmployeeConditionSubmissionDetail
              key={submission.id}
              employeeConditionSubmission={submission}
              className="mt-12"
              openReply={() => {
                setSelectGetReplyId(submission.id);
                setReplyOpen();
              }}
              refetch={refetch}
              canDelete
            />
          ))}
        </InfiniteScrollWithMoreButton>
      </EmployeeLayoutMainContent>
      <EmployeeLayoutRightSidebar>
        <RightSidebarToggleIconWithLabel
          iconOnClick={searchOpenToggle}
          handleOnClose={setClose}
          label={currentOpenLabel}
          isOpen={isOpen}
        >
          {isSearchOpen && (
            <Form onSubmit={onSearchSubmit} className="mt-6 space-y-6">
              <TitleWithChild title="期間指定">
                <SelectDateRangeButtons
                  startDate={startDate}
                  endDate={endDate}
                  onChange={onChangeDateRange}
                  className="mt-3.5"
                />
              </TitleWithChild>
              <TitleWithChild
                title="部署・課・ユーザー"
                className="mt-2 space-y-6"
              >
                <DivisionAndSectionAndEmployeesDropdownField
                  selectableDropdownDivisions={selectableDropdownDivisions}
                  selectedDropdownDivisions={selectedDropdownDivisions}
                  onDivisionChange={onDivisionChange}
                  selectableDropdownSections={selectableDropdownSections}
                  selectedDropdownSections={selectedDropdownSections}
                  onSectionChange={onSectionChange}
                  selectableDropdownEmployees={selectableDropdownEmployees}
                  selectedDropdownEmployees={selectedDropdownEmployees}
                  onEmployeesChange={onEmployeesChange}
                />
              </TitleWithChild>
              <TitleWithChild title="タイトル">
                <MultipleDropDownField
                  name="employeeConditionSurveyIds"
                  options={selectableEmployeeConditionSurveys}
                  value={selectedEmployeeConditionSurveys}
                  onChange={onChangeEmployeeConditionSurveys}
                />
              </TitleWithChild>
              <TitleWithChild title="リプライ">
                <DropDownField
                  name="hasReply"
                  options={selectableDropdownHasReplyIds}
                  value={selectedDropdownHasReplyId}
                  onChange={onChangeDropdownHasReplyId}
                />
              </TitleWithChild>
              <TitleWithChild title="自分のブックマーク">
                <DropDownField
                  name="hasBookmark"
                  options={selectableDropdownHasBookmarkIds}
                  value={selectedDropdownHasBookmarkId}
                  onChange={onChangeDropdownHasBookmarkId}
                />
              </TitleWithChild>
              <TitleWithChild title="詳細条件">
                <LabelWithCheckBox
                  labelText="過去所属先での対応含む"
                  labelEnd
                  checked={isBelongingEmployee}
                  name="hasBookmark"
                  onChange={onChangeIsBelongingEmployee}
                />
              </TitleWithChild>
              <TitleWithChild title="フリーワード">
                <TextField
                  value={keyword}
                  placeholder="キーワード"
                  type="search"
                  name="keyword"
                  onChange={onChangeKeyword}
                />
              </TitleWithChild>
              <div className="space-x-4 flex">
                <Button
                  text="リセット"
                  color="gray"
                  outline
                  className="w-full"
                  onClick={onConditionReset}
                />
                <FormSubmitButton
                  value="検索する"
                  color="primary"
                  className="w-full"
                />
              </div>
            </Form>
          )}
          {isReplyOpen && repliesIndexData && (
            <>
              <EmployeeConditionSubmissionRepliesWithEmployeeConditionSubmission
                className="mt-6"
                employeeConditionSubmission={
                  repliesIndexData.employeeConditionSubmission
                }
                mentions={[]}
                employeeConditionSubmissionReplies={
                  repliesIndexData.employeeConditionSubmissionReplies || []
                }
                refetch={repliesRefetch}
              />
            </>
          )}
        </RightSidebarToggleIconWithLabel>
      </EmployeeLayoutRightSidebar>
    </EmployeeLayout>
  );
};
